import { FLIGHT_DELAY_THRESHOLD_MINUTES, type Flight, type JourneyConnection, type TravelDirection } from "@seagames/contracts";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { formatDateTime, formatDayShort } from "../../../lib/format/datetime";
import { currentLocale } from "../../../lib/i18n";
import { ModeIcon, PlaneIcon } from "../../travel-leg-form/icons";
import { changedTime, terminalLabel } from "../../travel-leg-form/model";
import { AirlineLogo, useAirlineName } from "../airline-logo";
import {
	type CardEnd,
	type CardStatus,
	type CardTone,
	type FlightCardModel,
	airportZone,
	cardFromFlight,
	cardStatus,
	dayOffset,
	durationMinutes,
	endTime,
	splitDuration,
	zoneLabel,
} from "./model";

/**
 * The A&D flight card (approved mock, 2026-11-03), one family in four sizes:
 *
 *   list     the journey list — airline · route · the caller's `meta` column
 *   large    the journey detail's header — the same, bigger, each end's zone and day written out
 *   line     the record page's Travel step — one line
 *   compact  the leg form's lookup hit / a connection row — airline row, then the route
 *
 * Each end's time is its AIRPORT's local time with the zone written beside
 * it (never hover-only): a deliberate exception to the viewer-zone rule, the
 * card only (see `model.ts`). The viewer's own reading is the tooltip. A card
 * with no flight on record is the degraded "Not tracked" card (dashed, no
 * take-off time). The airline's logo and name come from the screen's ONE
 * `useAirlineLogos` map (`AirlineLogosProvider` above the cards).
 *
 * Layout breakpoints are CONTAINER queries: put `@container` on the list /
 * the detail's wrapper, so the card lays out by the room it has, not the
 * window (the sidebar takes a share of it).
 */

export type FlightCardSize = "list" | "large" | "line" | "compact";

const TONE: Record<CardTone, string> = {
	ok: "bg-gms-ok-soft text-gms-ok",
	warn: "bg-gms-warn-soft text-gms-warn",
	navy: "bg-gms-navy-soft text-gms-navy",
	bad: "bg-gms-bad-soft text-gms-bad",
	mute: "bg-gms-soft-2 text-gms-ink-2",
	line: "border border-dashed border-gms-line-2 bg-transparent text-gms-ink-2",
};

const chipBox = "inline-flex h-[1.375rem] shrink-0 items-center gap-[0.3125rem] rounded-full px-[0.5rem] text-[0.6875rem] font-semibold whitespace-nowrap";

/** The card's status chip — every value of the approved set (`cardStatus`). */
export function CardStatusChip({ status, zoneOfLanding, className = "" }: { status: CardStatus; zoneOfLanding?: CardEnd["zone"]; className?: string }) {
	const { t } = useTranslation("travel");
	const landed = status.key === "landed" && status.at && zoneOfLanding ? endTime(status.at, zoneOfLanding) : null;
	const label =
		status.key === "delayed" && status.minutes !== null
			? t("flightCard.status.delayedBy", { count: status.minutes, n: status.minutes })
			: landed
				? t("flightCard.status.landedAt", { time: landed.clock })
				: t(`flightCard.status.${status.key}`);
	return (
		<span title={t(`flightCard.statusHint.${status.key}`, { minutes: FLIGHT_DELAY_THRESHOLD_MINUTES })} className={`${chipBox} ${TONE[status.tone]} ${className}`}>
			{status.key !== "not_tracked" && <span aria-hidden className="size-[0.375rem] rounded-full bg-current" />}
			{label}
		</span>
	);
}

/** "Manual times": a desk set the flight's times by hand (never shown to a NOC — the caller decides). */
export function ManualTimesChip({ className = "" }: { className?: string }) {
	const { t } = useTranslation("travel");
	return (
		<span title={t("flightCard.manualHint")} className={`${chipBox} ${TONE.line} ${className}`}>
			{t("flightCard.manual")}
		</span>
	);
}

/** The status chip, plus "Manual times" when set by hand (`showManual` = false for a NOC). */
function StatusChips({ model, showManual }: { model: FlightCardModel; showManual: boolean }) {
	const status = cardStatus(model);
	return (
		<>
			<CardStatusChip status={status} zoneOfLanding={model.to.zone} />
			{showManual && model.tracked && model.manual && <ManualTimesChip />}
		</>
	);
}

/** "2h 05m" / "45m" / "3h". */
function useDurationText() {
	const { t } = useTranslation("travel");
	return (minutes: number): string => {
		const { h, m } = splitDuration(minutes);
		if (h === 0) return t("flightCard.durationM", { m });
		if (m === 0) return t("flightCard.durationH", { h });
		return t("flightCard.durationHm", { h, m: String(m).padStart(2, "0") });
	};
}

/** The zone an end's time is in, as words: "GMT+8" / "MYT", "your time" (airport zone unknown), "MYT · typed". */
function useZoneText() {
	const { t } = useTranslation("travel");
	return (end: CardEnd): string => {
		const at = end.at ? new Date(end.at) : new Date();
		if (end.zone.kind === "viewer") return t("flightCard.yourTime");
		const zone = zoneLabel(end.zone.timeZone, currentLocale(), at);
		return end.zone.kind === "typed" ? t("flightCard.zoneTyped", { zone }) : zone;
	};
}

/** "Direct" / "1 stop · SIN" / "2 stops · SIN, BKK" (+ "· 35 min change" or "· at risk" when the change is at risk). */
function useStopsText() {
	const { t } = useTranslation("travel");
	return (m: FlightCardModel): string => {
		const n = m.via.length;
		if (n === 0) return m.tracked ? t("flightCard.direct") : t("flightCard.typedTime");
		const airports = m.via.join(", ");
		if (m.viaAtRisk && m.changeMinutes !== null) return t("flightCard.stopsChange", { count: n, airports, minutes: m.changeMinutes });
		if (m.viaAtRisk) return t("flightCard.stopsAtRisk", { count: n, airports });
		return t("flightCard.stops", { count: n, airports });
	};
}

/** The airline mark: its logo / code badge, or a dashed glyph of the mode when no flight is on record. */
function Mark({ model, size }: { model: FlightCardModel; size: "sm" | "md" | "lg" }) {
	const { t } = useTranslation("travel");
	if (model.carrier) return <AirlineLogo code={model.carrier} size={size} />;
	const box = size === "sm" ? "size-[1.5rem] rounded-[0.3125rem]" : size === "lg" ? "size-[2.75rem] rounded-[0.5625rem]" : "size-[2rem] rounded-[0.4375rem]";
	return (
		<span title={t("flightCard.noFlight")} aria-hidden className={`inline-flex shrink-0 items-center justify-center border border-dashed border-gms-line-2 text-gms-ink-3 ${box}`}>
			<ModeIcon mode={model.mode} className="size-[55%]" />
		</span>
	);
}

/** Who flies it: the mark, the airline (or "No flight on record"), the flight number, the direction. */
function Who({ model, size, dirLine }: { model: FlightCardModel; size: "list" | "large" | "compact"; dirLine?: string | undefined }) {
	const { t } = useTranslation("travel");
	const airline = useAirlineName(model.carrier);
	const big = size === "large";
	const top = model.tracked ? airline : t("flightCard.noFlight");
	return (
		<span className="flex min-w-0 items-center gap-[0.625rem]">
			<Mark model={model} size={big ? "lg" : "md"} />
			<span className="grid min-w-0 leading-[1.3]">
				{top && <span className={`truncate font-semibold ${model.tracked ? "text-gms-navy-ink" : "text-gms-ink-3"} ${big ? "text-[0.9375rem]" : "text-[0.8125rem]"}`}>{top}</span>}
				<span className={`font-mono font-medium text-gms-ink-2 ${big ? "text-[0.875rem]" : "text-[0.75rem]"}`}>{model.name ?? t(`mode.${model.mode}`)}</span>
				{dirLine && <span className="mt-[0.125rem] text-[0.6875rem] font-bold tracking-[0.04em] text-gms-ink-3 uppercase">{dirLine}</span>}
			</span>
		</span>
	);
}

/** One end: the time (scheduled struck through when it moved, "+1" on a later day), its zone in words, the airport. */
function End({ end, size, align, plus = 0 }: { end: CardEnd; size: "list" | "large" | "compact"; align: "start" | "end"; plus?: number }) {
	const { t } = useTranslation("travel");
	const zoneText = useZoneText()(end);
	const time = endTime(end.at, end.zone);
	const was = end.scheduled && changedTime(end.scheduled, end.at) ? endTime(end.scheduled, end.zone) : null;
	const timeCls =
		size === "large"
			? "text-[1.875rem] @max-[30rem]:text-[1.375rem]"
			: size === "compact"
				? "text-[1.0625rem]"
				: "text-[1.375rem] @max-[30rem]:text-[1.125rem]";
	const right = align === "end";
	const landsOn = plus !== 0 && time ? formatDayShort(time.date) : null;
	// A typed service's far end: no code, no time, only the place as typed ("kota kinabalu").
	// The place is the end's headline, in the text font — not a big "—" over a lone name.
	if (!time && !end.code && end.place) {
		const nameCls = size === "large" ? "text-[1.0625rem] @max-[30rem]:text-[0.9375rem]" : size === "compact" ? "text-[0.875rem]" : "text-[0.9375rem] @max-[30rem]:text-[0.8125rem]";
		return (
			<span className={`grid min-w-0 ${right ? "justify-items-end text-right" : ""}`}>
				<span title={end.place} className={`block max-w-[12rem] truncate leading-[1.25] font-semibold text-gms-navy-ink ${nameCls}`}>
					{end.place}
				</span>
				<span className="text-[0.6875rem] text-gms-ink-3">{t("flightCard.noTime")}</span>
			</span>
		);
	}
	const place = (
		<span className={`block truncate text-[0.75rem] text-gms-ink-2 ${right ? "text-right" : ""}`}>
			{end.code && <b className="mr-[0.25rem] font-mono font-bold text-gms-navy-ink">{end.code}</b>}
			{end.terminal && !end.place && <span className="mr-[0.25rem]">{terminalLabel(end.terminal)}</span>}
			{end.place && <span title={end.place}>{end.place}</span>}
			{!end.code && !end.place && <span className="text-gms-ink-3">—</span>}
		</span>
	);
	return (
		<span className={`grid min-w-0 ${right ? "justify-items-end text-right" : ""}`}>
			<span className={`inline-flex flex-wrap items-baseline gap-x-[0.3125rem] ${right ? "justify-end" : ""}`}>
				{was && (
					<del className="text-[0.75rem] text-gms-ink-3 tabular-nums decoration-gms-ink-3/70">
						<span className="sr-only">{t("flightCard.scheduledWas")}</span>
						{was.clock}
					</del>
				)}
				{time ? (
					<time
						dateTime={end.at ?? undefined}
						title={formatDateTime(end.at)}
						className={`leading-[1.1] font-bold tracking-[-0.01em] tabular-nums ${timeCls} ${was ? "text-gms-warn" : "text-gms-navy-ink"}`}
					>
						{time.clock}
					</time>
				) : (
					<span className={`leading-[1.1] font-bold text-gms-ink-3 ${timeCls}`}>
						<span aria-hidden>—</span>
						<span className="sr-only">{t("flightCard.noTime")}</span>
					</span>
				)}
				{plus !== 0 && landsOn && (
					<sup title={t("flightCard.landsOn", { day: landsOn })} className="text-[0.6875rem] font-bold text-gms-bad">
						<span aria-hidden>{plus > 0 ? `+${plus}` : String(plus)}</span>
						<span className="sr-only">{t("flightCard.landsOn", { day: landsOn })}</span>
					</sup>
				)}
				{/* The zone in words, beside the time (list / compact); the large card writes it out under the place. */}
				{size !== "large" && time && <span className="text-[0.6875rem] font-medium text-gms-ink-3">{zoneText}</span>}
			</span>
			{place}
			{size === "large" && time && <span className="text-[0.75rem] text-gms-ink-3">{t("flightCard.zoneDay", { zone: zoneText, day: formatDayShort(time.date) })}</span>}
		</span>
	);
}

/** Between the ends: how long, the track (a dot per change, red when at risk), and "Direct" / "1 stop · SIN". */
function Middle({ model }: { model: FlightCardModel }) {
	const { t } = useTranslation("travel");
	const duration = durationMinutes(model);
	const durationText = useDurationText();
	const stops = useStopsText()(model);
	return (
		<span className="grid min-w-0 justify-items-center text-center">
			<span className="text-[0.71875rem] whitespace-nowrap text-gms-ink-3 tabular-nums">{duration !== null ? durationText(duration) : model.tracked ? "" : t("flightCard.durationUnknown")}</span>
			<span aria-hidden className="relative my-[0.125rem] flex h-[1rem] w-full items-center">
				<span className={`absolute top-1/2 right-[0.875rem] left-0 border-t border-gms-line-2 ${model.tracked ? "" : "border-dashed"}`} />
				{model.via.map((code, i) => (
					<span
						key={code}
						style={{ left: `${((i + 1) / (model.via.length + 1)) * 100}%` }}
						className={`absolute top-1/2 -mt-[0.25rem] -ml-[0.25rem] size-[0.5rem] rounded-full border-2 ${model.viaAtRisk ? "border-gms-bad bg-gms-bad" : "border-gms-ink-3 bg-base-100"}`}
					/>
				))}
				<PlaneIcon className="absolute right-0 size-[0.875rem] rotate-45 text-gms-ink-3" />
			</span>
			<span className={`max-w-full text-[0.71875rem] leading-[1.3] font-semibold ${model.viaAtRisk ? "text-gms-bad" : model.tracked ? "text-gms-ink-2" : "text-gms-ink-3"}`} title={stops}>
				{stops}
			</span>
			{model.otherRoutes > 0 && <span className="text-[0.6875rem] text-gms-ink-3">{t("flightCard.otherRoutes", { count: model.otherRoutes, n: model.otherRoutes })}</span>}
		</span>
	);
}

/** The route row: from — the middle — to. */
function Route({ model, size }: { model: FlightCardModel; size: "list" | "large" | "compact" }) {
	const plus = dayOffset(model.from, model.to);
	return (
		<span className="grid min-w-0 grid-cols-[minmax(0,max-content)_minmax(2.5rem,1fr)_minmax(0,max-content)] items-center gap-[0.75rem] @max-[30rem]:gap-[0.5rem]">
			<End end={model.from} size={size} align="start" />
			<Middle model={model} />
			<End end={model.to} size={size} align="end" plus={plus} />
		</span>
	);
}

/** The frame's classes: a red left edge when `alarm`, dashed and grey when nothing is tracked. */
export function flightCardFrame(model: FlightCardModel, alarm: boolean): string {
	return [
		"rounded-[0.625rem] border",
		model.tracked ? "border-gms-line bg-base-100" : "border-dashed border-gms-line-2 bg-gms-soft",
		alarm ? "border-l-[0.25rem] border-l-gms-bad" : "",
	].join(" ");
}

/**
 * The list / large card's three areas (who + status, route, `meta`). The
 * frame (border, red edge) is the caller's — a list wraps it in its link with
 * `flightCardFrame`, the detail draws it inside its own section.
 */
export function FlightCardBody({
	model,
	size,
	meta,
	dirLine,
	showManual,
	extraChips,
}: {
	model: FlightCardModel;
	size: "list" | "large";
	/** The right-hand column: people, pickup, readiness — the placement's own. */
	meta?: ReactNode;
	/** Under the flight number: "Arrival", "Arrival · Mon 23/11/2026". */
	dirLine?: string;
	/** "Manual times" chip — false for a NOC. */
	showManual: boolean;
	extraChips?: ReactNode;
}) {
	const large = size === "large";
	return (
		<span
			className={`grid grid-cols-[minmax(0,1fr)] items-center gap-x-[1.25rem] gap-y-[0.75rem] ${
				large
					? "@min-[44rem]:grid-cols-[14rem_minmax(0,1fr)_15rem]"
					: "@min-[56rem]:grid-cols-[13rem_minmax(0,44rem)_minmax(17rem,1fr)] @min-[90rem]:grid-cols-[15rem_minmax(0,46rem)_minmax(22rem,1fr)]"
			}`}
		>
			<span
				className={`flex min-w-0 items-start justify-between gap-[0.5rem] ${large ? "@min-[44rem]:flex-col @min-[44rem]:justify-start" : "@min-[56rem]:flex-col @min-[56rem]:justify-start"}`}
			>
				<Who model={model} size={size} dirLine={dirLine} />
				<span className="flex min-w-0 flex-wrap items-center justify-end gap-[0.3125rem]">
					<StatusChips model={model} showManual={showManual} />
					{extraChips}
				</span>
			</span>
			<Route model={model} size={size} />
			{meta && (
				<span
					className={`grid min-w-0 gap-[0.375rem] ${large ? "@min-[44rem]:border-l @min-[44rem]:border-gms-line @min-[44rem]:pl-[1.25rem]" : "@min-[56rem]:border-l @min-[56rem]:border-gms-line @min-[56rem]:pl-[1.25rem]"}`}
				>
					{meta}
				</span>
			)}
		</span>
	);
}

/**
 * The record page's one line: mark, flight number, "MNL 8:15AM GMT+8 → KUL
 * 12:10PM MYT", "· 3h 55m · Direct", the status. `sub` is the line under it
 * (day, entry point).
 */
export function FlightCardLine({ model, showManual, sub }: { model: FlightCardModel; showManual: boolean; sub?: ReactNode }) {
	const { t } = useTranslation("travel");
	const duration = durationMinutes(model);
	const durationText = useDurationText();
	const zoneText = useZoneText();
	const stops = useStopsText()(model);
	const plus = dayOffset(model.from, model.to);
	const seg = (end: CardEnd, withPlus: number) => {
		const time = endTime(end.at, end.zone);
		const was = end.scheduled && changedTime(end.scheduled, end.at) ? endTime(end.scheduled, end.zone) : null;
		return (
			<span className="inline-flex items-baseline gap-x-[0.25rem] whitespace-nowrap tabular-nums">
				{end.code ? (
					<b className="font-mono font-bold text-gms-navy-ink">{end.code}</b>
				) : end.place ? (
					<span title={end.place} className="inline-block max-w-[12rem] truncate align-bottom font-medium text-gms-navy-ink">
						{end.place}
					</span>
				) : (
					<span className="text-gms-ink-3">—</span>
				)}
				{was && (
					<del className="text-[0.75rem] text-gms-ink-3 decoration-gms-ink-3/70">
						<span className="sr-only">{t("flightCard.scheduledWas")}</span>
						{was.clock}
					</del>
				)}
				{time && (
					<>
						<time dateTime={end.at ?? undefined} title={formatDateTime(end.at)} className={`font-semibold ${was ? "text-gms-warn" : "text-gms-navy-ink"}`}>
							{time.clock}
						</time>
						{withPlus !== 0 && (
							<sup className="text-[0.625rem] font-bold text-gms-bad" title={t("flightCard.landsOn", { day: formatDayShort(time.date) })}>
								<span aria-hidden>{withPlus > 0 ? `+${withPlus}` : String(withPlus)}</span>
								<span className="sr-only">{t("flightCard.landsOn", { day: formatDayShort(time.date) })}</span>
							</sup>
						)}
						<span className="text-[0.6875rem] font-medium text-gms-ink-3">{zoneText(end)}</span>
					</>
				)}
			</span>
		);
	};
	return (
		<span className="grid min-w-0 gap-[0.25rem]">
			<span className="flex min-w-0 flex-wrap items-center gap-x-[0.5rem] gap-y-[0.25rem] text-[0.8125rem]">
				<Mark model={model} size="sm" />
				<span className="font-mono font-semibold text-gms-navy-ink">{model.name ?? t(`mode.${model.mode}`)}</span>
				{seg(model.from, 0)}
				<span aria-hidden className="text-gms-ink-3">
					→
				</span>
				<span className="sr-only">{t("flightCard.to")}</span>
				{seg(model.to, plus)}
				{model.tracked && (
					<span className={`text-[0.75rem] ${model.viaAtRisk ? "font-semibold text-gms-bad" : "text-gms-ink-3"}`}>
						{duration !== null ? t("flightCard.lineTail", { duration: durationText(duration), stops }) : stops}
					</span>
				)}
				<StatusChips model={model} showManual={showManual} />
			</span>
			{sub && <span className="text-[0.71875rem] text-gms-ink-3">{sub}</span>}
		</span>
	);
}

/**
 * The leg form's lookup hit and a connection row: the airline row (mark,
 * flight number, its day, the status unless scheduled, `badge`, `action`),
 * then the route. Replaces the old `FlightCard` of `flight-status.tsx`.
 */
export function FlightCardCompact({ model, badge, action, showManual = true }: { model: FlightCardModel; badge?: ReactNode; action?: ReactNode; showManual?: boolean }) {
	const status = cardStatus(model);
	return (
		<div role="status" className="grid min-w-0 gap-[0.5rem] rounded-field border border-gms-line bg-gms-soft px-[0.875rem] py-[0.625rem] text-[0.8125rem] leading-[1.4] @container">
			<div className="flex min-w-0 flex-wrap items-center justify-between gap-x-[0.75rem] gap-y-[0.375rem]">
				<span className="inline-flex min-w-0 items-center gap-x-[0.5rem]">
					<Mark model={model} size="sm" />
					<span className="inline-flex min-w-0 flex-wrap items-baseline gap-x-[0.5rem]">
						<b className="font-mono font-semibold tracking-[0.02em] text-gms-navy-ink">{model.name}</b>
						{model.day && <span className="text-gms-ink-2">{formatDayShort(model.day)}</span>}
					</span>
				</span>
				<span className="flex flex-wrap items-center gap-[0.5rem]">
					{badge}
					{status.key !== "on_time" && <CardStatusChip status={status} zoneOfLanding={model.to.zone} />}
					{showManual && model.manual && <ManualTimesChip />}
					{action}
				</span>
			</div>
			<div className="rounded-[0.375rem] bg-base-100 px-[0.625rem] py-[0.5rem]">
				<Route model={model} size="compact" />
			</div>
		</div>
	);
}

/**
 * The journey detail's times strip (under the large card): take-off and
 * landing — scheduled · estimated · actual — each in its airport's own zone,
 * the zone written out.
 */
export function FlightTimesStrip({ flight, direction }: { flight: Flight; direction: TravelDirection }) {
	const { t } = useTranslation("travel");
	const zoneText = useZoneText();
	const m = cardFromFlight(direction, flight);
	const clock = (at: string | null, end: CardEnd) => endTime(at, end.zone)?.clock ?? "—";
	const row = (key: "takeOff" | "landing", end: CardEnd, s: string | null, e: string | null, a: string | null) => (
		<p className="flex flex-wrap items-baseline gap-x-[0.75rem] gap-y-[0.125rem]">
			<span className="tabular-nums">{t(`flightCard.${key}`, { scheduled: clock(s, end), estimated: clock(e, end), actual: clock(a, end) })}</span>
			<span className="text-gms-ink-3">{zoneText({ ...end, at: s ?? e ?? a })}</span>
		</p>
	);
	return (
		<div className="grid gap-[0.25rem] text-[0.75rem] text-gms-ink-2">
			{row("takeOff", m.from, flight.scheduled_departure_at, flight.estimated_departure_at, flight.actual_departure_at)}
			{row("landing", m.to, flight.scheduled_arrival_at, flight.estimated_arrival_at, flight.actual_arrival_at)}
		</div>
	);
}

/** A journey's transit paths (detail): "RGN 8:10AM GMT+6:30 → KUL 3:05PM MYT · via SIN · 4 people", the change (red when at risk). */
export function JourneyPaths({ connections, more }: { connections: readonly JourneyConnection[]; more: number }) {
	const { t } = useTranslation("travel");
	const zoneText = useZoneText();
	if (connections.length === 0) return null;
	const at = (iso: string | null, tz: string | null) => {
		const end: CardEnd = { code: null, place: null, terminal: null, at: iso, scheduled: null, zone: airportZone(tz) };
		const time = endTime(iso, end.zone);
		return time ? `${time.clock} ${zoneText(end)}` : "—";
	};
	return (
		<ul className="grid gap-[0.25rem] text-[0.75rem] text-gms-ink-2">
			{connections.map((c) => (
				<li key={`${c.via_iata.join(">")}:${c.first_origin_iata}:${c.last_destination_iata}`} className="flex flex-wrap items-baseline gap-x-[0.625rem] gap-y-[0.125rem]">
					<span className="tabular-nums">
						{t("flightCard.path", {
							count: c.people,
							n: c.people,
							from: c.first_origin_iata ?? "—",
							departs: at(c.first_departure_at, c.first_origin_time_zone),
							to: c.last_destination_iata ?? "—",
							arrives: at(c.last_arrival_at, c.last_destination_time_zone),
							via: c.via_iata.join(", "),
						})}
					</span>
					{c.min_change_minutes !== null && (
						<span className={c.at_risk ? "font-semibold text-gms-bad" : "text-gms-ink-3"}>{t("flightCard.pathChange", { minutes: c.min_change_minutes })}</span>
					)}
					{c.at_risk && <span className="font-semibold text-gms-bad">{t("legForm.transit.atRisk")}</span>}
				</li>
			))}
			{more > 0 && <li className="text-gms-ink-3">{t("flightCard.otherRoutes", { count: more, n: more })}</li>}
		</ul>
	);
}
